import React, { useCallback, useEffect, useState } from 'react';
import {
  listMyScripts,
  listScriptVouchers,
  saveCompletionReward,
  searchMerchants,
  vouchersToRows,
  voucherRowsToPayload,
  COUPON_TYPE_OPTIONS,
} from '../../lib/rewardVoucherApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnRow,
  BtnDanger,
  Card,
  Table,
  Badge,
  ErrorText,
  Field,
} from '../../styles/adminStyles';

function emptyRow() {
  return {
    id: null,
    title: '',
    description: '',
    couponType: 'free_item',
    valueDollars: '',
    discountPercent: '',
    winnerLimit: '1',
    merchantUserId: null,
    merchantBrandName: null,
    status: null,
  };
}

function VoucherStatusBadge({ status }) {
  const { t } = usePortalT();
  if (!status) return null;
  const map = {
    authenticated: ['voucherAuth.statusAuthenticated', 'active'],
    rejected: ['voucherAuth.statusRejected', 'ended'],
    pending: ['voucherAuth.statusPending', 'draft'],
  };
  const [key, tone] = map[status] || [null, 'draft'];
  return <Badge $status={tone}>{key ? t(key) : status}</Badge>;
}

export default function RouteRewardsPage() {
  const { t, tEnum } = usePortalT();
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);

  // Merchant picker
  const [merchantQuery, setMerchantQuery] = useState('');
  const [merchantResults, setMerchantResults] = useState([]);
  const [pickerRowIndex, setPickerRowIndex] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setScripts(await listMyScripts());
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const openEditor = async (script) => {
    setSelected(script);
    setSaveMsg(null);
    setEditLoading(true);
    try {
      const vouchers = await listScriptVouchers(script.id);
      const mapped = vouchersToRows(vouchers);
      setRows(mapped.length ? mapped : [emptyRow()]);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('routeRewards.loadRewardFailed'));
      setRows([emptyRow()]);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditor = () => {
    setSelected(null);
    setRows([]);
    setMerchantResults([]);
    setPickerRowIndex(null);
  };

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  const runMerchantSearch = async (idx) => {
    setPickerRowIndex(idx);
    const results = await searchMerchants(merchantQuery, 20);
    setMerchantResults(results);
  };

  const pickMerchant = (idx, m) => {
    updateRow(idx, { merchantUserId: m.userId, merchantBrandName: m.brandName || m.name });
    setPickerRowIndex(null);
    setMerchantResults([]);
    setMerchantQuery('');
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveMsg(null);
    const vouchers = voucherRowsToPayload(rows);
    const res = await saveCompletionReward(selected.id, {
      enabled: vouchers.length > 0,
      vouchers,
    });
    setSaving(false);
    if (res.success) {
      setSaveMsg({ ok: true, text: t('routeRewards.savedOk') });
      await load();
      // reload editor rows to reflect newly assigned ids / statuses
      await openEditor(selected);
    } else {
      setSaveMsg({ ok: false, text: res.error || t('common.saveFailed') });
    }
  };

  if (selected) {
    return (
      <>
        <PageTitle>{t('routeRewards.editorTitle')}</PageTitle>
        <PageSubtitle>
          {t('routeRewards.editorSubtitle', { title: selected.title })}
        </PageSubtitle>

        <BtnRow style={{ marginTop: 0, marginBottom: 16 }}>
          <Btn type="button" onClick={closeEditor}>{t('common.backToList')}</Btn>
        </BtnRow>

        {editLoading ? (
          <Card><p>{t('common.loading')}</p></Card>
        ) : (
          <>
            {rows.map((row, idx) => {
              const locked = Boolean(row.id); // existing vouchers are immutable
              return (
                <Card key={row.id || `new-${idx}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <strong>{t('routeRewards.voucherN', { n: idx + 1 })}</strong>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <VoucherStatusBadge status={row.status} />
                      {!locked ? (
                        <BtnDanger type="button" onClick={() => removeRow(idx)} style={{ padding: '6px 10px' }}>
                          {t('common.delete')}
                        </BtnDanger>
                      ) : null}
                    </div>
                  </div>

                  <Field>
                    {t('routeRewards.fTitle')}
                    <input
                      value={row.title}
                      disabled={locked}
                      onChange={(e) => updateRow(idx, { title: e.target.value })}
                      placeholder={t('routeRewards.fTitlePh')}
                    />
                  </Field>
                  <Field>
                    {t('routeRewards.fDescOptional')}
                    <textarea
                      value={row.description}
                      disabled={locked}
                      onChange={(e) => updateRow(idx, { description: e.target.value })}
                    />
                  </Field>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <Field>
                      {t('routeRewards.fType')}
                      <select
                        value={row.couponType}
                        disabled={locked}
                        onChange={(e) => updateRow(idx, { couponType: e.target.value })}
                      >
                        {COUPON_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{tEnum('couponType', o.value)}</option>
                        ))}
                      </select>
                    </Field>
                    {row.couponType === 'percentage' ? (
                      <Field>
                        {t('routeRewards.fDiscountPercent')}
                        <input
                          type="number"
                          value={row.discountPercent}
                          disabled={locked}
                          onChange={(e) => updateRow(idx, { discountPercent: e.target.value })}
                          placeholder="20"
                        />
                      </Field>
                    ) : (
                      <Field>
                        {t('routeRewards.fValueDollar')}
                        <input
                          type="number"
                          value={row.valueDollars}
                          disabled={locked}
                          onChange={(e) => updateRow(idx, { valueDollars: e.target.value })}
                          placeholder="5.00"
                        />
                      </Field>
                    )}
                    <Field>
                      {t('routeRewards.fQuota')}
                      <input
                        type="number"
                        value={row.winnerLimit}
                        disabled={locked}
                        onChange={(e) => updateRow(idx, { winnerLimit: e.target.value })}
                        placeholder="1"
                      />
                    </Field>
                  </div>

                  <Field>
                    {t('routeRewards.fStore')}
                    {row.merchantUserId ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span style={{ fontWeight: 700 }}>{row.merchantBrandName || row.merchantUserId}</span>
                        {!locked ? (
                          <Btn type="button" onClick={() => updateRow(idx, { merchantUserId: null, merchantBrandName: null })} style={{ padding: '6px 10px' }}>
                            {t('common.remove')}
                          </Btn>
                        ) : null}
                      </div>
                    ) : !locked ? (
                      <div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input
                            value={pickerRowIndex === idx ? merchantQuery : ''}
                            onChange={(e) => { setMerchantQuery(e.target.value); setPickerRowIndex(idx); }}
                            placeholder={t('routeRewards.searchStorePh')}
                          />
                          <Btn type="button" onClick={() => runMerchantSearch(idx)}>{t('common.search')}</Btn>
                        </div>
                        {pickerRowIndex === idx && merchantResults.length > 0 ? (
                          <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8 }}>
                            {merchantResults.map((m) => (
                              <div
                                key={m.userId}
                                onClick={() => pickMerchant(idx, m)}
                                style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
                              >
                                <strong>{m.brandName || m.name}</strong>
                                {m.username ? <span style={{ color: '#94a3b8' }}> · @{m.username}</span> : null}
                                {m.isOfficial ? <Badge $status="active" style={{ marginLeft: 8 }}>{t('routeRewards.certified')}</Badge> : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>{t('routeRewards.selfServe')}</span>
                    )}
                  </Field>

                  {locked ? (
                    <p style={{ color: '#94a3b8', fontSize: 12, margin: 0 }}>
                      {t('routeRewards.lockedNote')}
                    </p>
                  ) : null}
                </Card>
              );
            })}

            <BtnRow>
              <Btn type="button" onClick={addRow}>{t('routeRewards.addVoucher')}</Btn>
              <Btn $primary type="button" onClick={handleSave} disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </Btn>
            </BtnRow>

            {saveMsg ? (
              <p style={{ marginTop: 12, fontWeight: 700, color: saveMsg.ok ? '#15803d' : '#b91c1c' }}>
                {saveMsg.text}
              </p>
            ) : null}
          </>
        )}
      </>
    );
  }

  return (
    <>
      <PageTitle>{t('routeRewards.title')}</PageTitle>
      <PageSubtitle>
        {t('routeRewards.subtitle')}
      </PageSubtitle>

      {error ? <ErrorText>{error}</ErrorText> : null}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <p style={{ padding: 18 }}>{t('common.loading')}</p>
        ) : scripts.length === 0 ? (
          <p style={{ padding: 18, color: '#64748b' }}>{t('routeRewards.empty')}</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>{t('routeRewards.colRoute')}</th>
                <th>{t('routeRewards.colCity')}</th>
                <th>{t('routeRewards.colCompletionReward')}</th>
                <th>{t('routeRewards.colAction')}</th>
              </tr>
            </thead>
            <tbody>
              {scripts.map((s) => (
                <tr key={s.id}>
                  <td style={{ fontWeight: 700 }}>{s.title}</td>
                  <td>{s.city || '—'}</td>
                  <td>
                    {s.completionReward?.enabled ? (
                      <Badge $status="active">{t('routeRewards.enabled')}</Badge>
                    ) : (
                      <Badge $status="draft">{t('routeRewards.notSet')}</Badge>
                    )}
                  </td>
                  <td>
                    <Btn $primary type="button" onClick={() => openEditor(s)}>
                      {s.completionReward?.enabled ? t('routeRewards.editReward') : t('routeRewards.addReward')}
                    </Btn>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
